// Shared types and per-mount state for VideoJs and its hooks.
import type ChainNavButton from "@/common/videojs/ChainNavButton";
import type { ResolutionSource } from "@/common/videojs/resolutionTypes";
import type { TimelineMarker } from "@/common/videojs/timelineMarkerTypes";
import type { OnApplicationEvent } from "@/components/player/experienceView";
import type { ChainNavMeta } from "@/modules/chainNav";
import type { TemplateSizes } from "@/types/layout";
import type { ExperienceState } from "@/types/state";
import type Component from "video.js/dist/types/component";
import type Player from "video.js/dist/types/player";

export type VideoJsProps = {
  experienceState: ExperienceState;
  timelineName: unknown;
  onApplicationEvent: OnApplicationEvent;
};

/** The player with the members the plugins and the control bar add. */
export type VideoPlayer = Player & {
  controlBar: Component;
  markers: { set(markers: TimelineMarker[]): void };
  updateSrc(src: ResolutionSource[]): unknown;
  defaultResolution(res?: string | number | null): unknown;
};

export type FullscreenToggle = Component & {
  handleClick(event?: Event): void;
  _pixNativeHandleClick?: (event?: Event) => void;
};

export type FullscreenElement = Element & {
  webkitRequestFullscreen?: Element["requestFullscreen"];
  webkitRequestFullScreen?: Element["requestFullscreen"];
};

export type FullscreenDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: Document["exitFullscreen"];
};

export type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
};

export interface NavPreview {
  el: HTMLDivElement;
  btn: ChainNavButton;
  resolveItem: () => ChainNavMeta | null;
  hasThumb: boolean;
  onEnter: () => void;
  onLeave: () => void;
}

export interface AutoplayAttempt {
  timer: ReturnType<typeof setTimeout>;
}

/** Per-mount state that the handlers read at call time (the class's instance fields). */
export interface VideoJsSelf {
  props: VideoJsProps;
  player: VideoPlayer | null;
  videoHost: HTMLDivElement | null;
  videoElement: Element | null;
  idexperience: unknown;
  timelineName: unknown;
  appliedExperienceLoaded: boolean;
  pendingUserPlay: boolean;
  mediaReadyExperienceId: unknown;
  // Cached intrinsic aspect of the poster (== the video frame), the fallback
  // updateVideoContentRect uses to anchor overlays to the video content rect
  // before videoWidth is known (still loading) or when the video errors and
  // never reports its dimensions.
  posterAspectW: number;
  posterAspectH: number;
  prevNavBtn: ChainNavButton | null;
  nextNavBtn: ChainNavButton | null;
  // YouTube-style hover previews for the prev/next arrows.
  prevPreview: NavPreview | null;
  nextPreview: NavPreview | null;
  // How the viewer entered fullscreen. `fsEnterMethod` is set by whichever
  // trigger fired (button / double-click); the event is emitted on the actual
  // enter (trackFullscreenEnter). `fsWasActive` guards the false→true
  // transition so we emit exactly once per enter.
  fsEnterMethod: "button" | "double_click" | null;
  fsWasActive: boolean;
  articleTime: number;
  percentComplete: number;
  isSeeking: boolean;
  // NaN until the first `seeking`; Math.floor(NaN) keeps a Time-Shift from firing.
  seekStart: number;
  videoEndedFired: boolean;
  videoEndedPending: boolean;
  didSeekToParam: boolean;
  lastFsToggle: number | undefined;
  currentAutoplayAttempt: AutoplayAttempt | null;
  cancelCtrlRecomposite: (() => void) | null;
  onDocFullscreenChange: (() => void) | null;
  unsubscribeChain: (() => void) | undefined;
}

export const REGION = "Video JS";

export function createVideoJsSelf(props: VideoJsProps): VideoJsSelf {
  return {
    props,
    player: null,
    videoHost: null,
    videoElement: null,
    idexperience: undefined,
    timelineName: undefined,
    appliedExperienceLoaded: false,
    pendingUserPlay: false,
    mediaReadyExperienceId: null,
    posterAspectW: 0,
    posterAspectH: 0,
    prevNavBtn: null,
    nextNavBtn: null,
    prevPreview: null,
    nextPreview: null,
    fsEnterMethod: null,
    fsWasActive: false,
    articleTime: -1,
    percentComplete: 0,
    isSeeking: false,
    seekStart: Number.NaN,
    videoEndedFired: false,
    videoEndedPending: false,
    didSeekToParam: false,
    lastFsToggle: undefined,
    currentAutoplayAttempt: null,
    cancelCtrlRecomposite: null,
    onDocFullscreenChange: null,
    unsubscribeChain: undefined,
  };
}

export function templateSizes(state: ExperienceState): TemplateSizes {
  return (state.template as { getSizes(): TemplateSizes }).getSizes();
}
